<script setup lang="ts">
import { useRouter } from "vue-router";
import { computed, ref, watch } from "vue";

import { caseFileService } from "@/services";

import { useBrandStore } from "@/stores/brand-store";
import { useCaseFileStore, type QPackForm } from "@/stores/case-file-store";

import { useWindow } from "@/hooks/use-window";
import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import Loading from "@/components/Loading.vue";

import { joinPaths, loadVendorFiles } from "./question-pack-loader";

const QPACK_VERSION_SOUTHWARK = "southwark-v1.3.11";
const QPACK_VERSION_GENERIC = "generic-v4.1.11";

import type { DockviewPanelComponentProps, DockviewPanelComponentEmits } from "@/hooks/use-case-panel";

defineEmits<DockviewPanelComponentEmits>();
withDefaults(defineProps<DockviewPanelComponentProps<false>>(), {  })

let viewerIncludesJSON: Record<"css" | "js", string[]> | undefined;

const router = useRouter();
const window = useWindow();
const routeParams = useCaseRouteParams();

const brandStore = useBrandStore();
const caseFileStore = useCaseFileStore();

type DISPLAY_MODE = "display" | "edit";

const rerenderKey = ref(0);
const formState = ref<
  | { status: "idle" }
  | { status: "loading-scripts"; loaded: number; total: number }
  | { status: "rendering" }
  | { status: "ready"; fileReference: string }
  | { status: "saving" }
  | { status: "error"; message: string }
>({ status: "idle" });
const formMode = ref<{ fileReference: string; mode: Exclude<DISPLAY_MODE, "display"> }>();

const isVisible = computed(() => __props.dockview?.isVisible ?? true);
const fileForm = computed(() => caseFileStore.fileForm ?? ({ status: "loading", form: undefined } as never));

const config = computed(() => brandStore.viewerConfig.questionPack ?? { module: undefined });

const qPackPublicPath = computed(
  () => `/questionpack-${config.value.module === "southwark" ? QPACK_VERSION_SOUTHWARK : QPACK_VERSION_GENERIC}/`,
);

watch(
  [fileForm, isVisible, rerenderKey],
  (_a, _b, onCleanup) => {
    const activeFileReference = routeParams.value.casefile;
    if (
      activeFileReference == null ||
      (formState.value.status === "ready" && formState.value.fileReference === activeFileReference) ||
      !isVisible.value
    )
      return;

    formState.value = { status: "idle" };
    window.value.document.getElementById("questionpack")?.replaceChildren();

    const form = fileForm.value.form;
    if (form == null) return;

    const formDisplayMode = formMode.value?.fileReference !== activeFileReference ? undefined : formMode.value;

    const controller = new AbortController();
    onCleanup(() => controller.abort());

    const instancePromise =
      viewerIncludesJSON == null
        ? fetch(joinPaths(qPackPublicPath.value, "viewer-includes.json"), { signal: controller.signal })
            .then((res) => {
              if (!res.ok) throw res;
              return res.json() as Promise<Record<"css" | "js", string[]>>;
            })
            .then((includes) => (viewerIncludesJSON = includes))
        : Promise.resolve(viewerIncludesJSON);

    void instancePromise
      .then(async (includes) => {
        controller.signal.throwIfAborted();
        formState.value = { status: "loading-scripts", loaded: 0, total: includes.js.length };

        try {
          await loadVendorFiles(window.value.document, qPackPublicPath.value, includes, (idx) => {
            controller.signal.throwIfAborted();
            if (formState.value.status !== "loading-scripts")
              throw new Error("Form state changed unexpectedly while loading scripts");
            formState.value = { ...formState.value, loaded: idx };
          });
        } catch (e) {
          console.error(e);
          throw new Error("An error occurred while loading the form scripts. Please report this to support.");
        }
      })
      .then(() => {
        controller.signal.throwIfAborted();
        formState.value = { status: "rendering" };

        try {
          formMode.value = formDisplayMode;
          window.value.qpackProvider = getQpackProvider(activeFileReference, formDisplayMode?.mode ?? "display", form);
          window.value.jQuery(window.value.loadQuestionPack);

          formState.value = { status: "ready", fileReference: activeFileReference };
        } catch (e) {
          console.error(e);
          throw new Error("An error occurred while rendering the form. Please report this to support.");
        }
      })
      .catch((e) => {
        if (controller.signal.aborted) return;

        window.value.qpackProvider = undefined as never;

        console.error(e);
        formState.value = {
          status: "error",
          message:
            e instanceof Response
              ? e.status === 404
                ? "The manifest file wasn't found. Please report this to support."
                : "The manifest file couldn't be loaded. Please report this to support."
              : e instanceof Error
                ? e.message
                : "An unexpected error occurred. Please report this to support.",
        };
      });

    if (import.meta.env.DEV) {
      if (config.value.css != null || config.value.js != null)
        alert("Question Pack configuration contains deprecated properties. Please report this to be updated.");
      else if (config.value.module == null)
        alert("Question Pack module is not configured for this brand. Please report for this to be configured.");
      else if (config.value.module !== "southwark" && config.value.module !== "generic")
        alert(
          `Question Pack module is configured to an unknown value: ${config.value.module as string}. Please report for this to be corrected.`,
        );
    }
  },
  { immediate: true },
);

function getQpackProvider(fileReference: string, mode: DISPLAY_MODE, form: QPackForm) {
  const config: typeof globalThis.window.qpackProvider = {
    getQuestionPack: function () {
      return JSON.stringify(form.schema.fields);
    },
    getQpackAnswers: function () {
      return JSON.stringify(form.answers);
    },
    getRecordingTimestamp: function () {
      return -3;
    },
    getRenderMode: function () {
      return mode;
    },
    rendererHostIdentifier: function () {
      return "";
    },
    setQpackAnswers: async function (newAnswers) {
      const formFileReference = caseFileStore.cachedFilesReferences[fileReference]?.form;
      if (formFileReference == null) return;

      try {
        formState.value = { status: "saving" };

        await caseFileService.saveForm(
          routeParams.value.client,
          routeParams.value.brand,
          routeParams.value.casename,
          fileReference,
          formFileReference,
          newAnswers,
        );

        caseFileStore.setFormAnswers(fileReference, JSON.parse(newAnswers) as QPackForm["answers"]);
      } catch (e) {
        // ignore for now
        console.error(e);
      }

      reloadForm();
    },
    displayPostRender: function (alpacaForm) {
      const isHTMLElement = (el: Node): el is HTMLElement => el.nodeType === Node.ELEMENT_NODE;
      const isSelectElement = (el: Node): el is HTMLSelectElement => isHTMLElement(el) && el.tagName === "SELECT";

      alpacaForm.children.forEach((child) => {
        if (child.isHidden()) return;

        const fieldControl = (["checkbox", "radio"].includes(child.type)
          ? child.control?.toArray().find((el) => isHTMLElement(el) && el.querySelector("input"))
          : child.control?.[0]) as unknown as HTMLElement | null;
        if (fieldControl == null) return;

        if (isSelectElement(fieldControl)) {
          const wrapper = window.value.document.createElement("div");
          fieldControl.insertAdjacentElement("beforebegin", wrapper);

          wrapper.tabIndex = 0;
          wrapper.ariaLabel = [...fieldControl.selectedOptions]
            .map((option) => option.innerText || option.value)
            .join(", ");

          wrapper.appendChild(fieldControl);
        } else if (child.type === "checkbox" || child.type === "radio") {
          child.control?.toArray().forEach((element) => {
            if (!isHTMLElement(element)) return;

            const input = element.querySelector(
              "input[type=checkbox],input[type=radio]",
            ) as unknown as HTMLInputElement;
            if (input == null) return;

            element.tabIndex = 0;
            element.ariaLabel = `${(input.labels?.[0]?.innerText || input.value).trim()} (${input.checked ? "Checked" : "Unchecked"})`;
          });
        }

        fieldControl.tabIndex = 0;

        const fieldLabel = child.field?.[0].querySelector("label");
        if (fieldLabel != null) fieldLabel.tabIndex = 0;
      });

      if (form.answers.__editEvents != null) {
        const editEvents = form.answers.__editEvents;

        const list = new Set<string>();
        for (const { startTime, endTime, path } of editEvents) {
          // Ensure the events have a defined start and end, and haven't been processed
          if (startTime == null || endTime == null || list.has(path)) continue;

          list.add(path);
          const alpacaField = alpacaForm.getControlByPath(path);

          const elField = alpacaField.field?.[0];
          if (elField == null) continue;

          const triggerWrapper = window.value.document.createElement("div");
          const trigger = window.value.document.createElement("div");
          const icon = window.value.document.createElement("i");

          icon.className = "fas fa-play";
          icon.ariaHidden = "true";

          trigger.tabIndex = 0;
          trigger.role = "button";
          trigger.ariaLabel = "Play Audio Sections";
          trigger.ariaDescription = "Click to play recorded sections of audio while this field was edited";
          trigger.className =
            "!tw-text-[0.9rem] tw-px-2 tw-py-0.5 tw-mr-1.5 tw-border tw-border-black tw-rounded-md tw-leading-none";

          trigger.appendChild(icon);
          trigger.addEventListener("click", () => {
            const { brand, casename } = routeParams.value;

            router.push({
              name: "casefilefieldbookmark",
              // remove initial slash from start of path
              params: { brand, casename, casefile: fileReference, fieldPath: path.replace(/^\//, "") },
              force: true,
            });
          });

          triggerWrapper.className = "tw-w-min";

          triggerWrapper.appendChild(trigger);

          const label = elField?.querySelector<HTMLLabelElement>("& > label");
          const controls = alpacaField.control?.toArray().filter(isHTMLElement) ?? [];

          const controlsWrapper = window.value.document.createElement("div");

          elField.style.display = "grid";
          elField.style.gridTemplateColumns = "min-content minmax(0, 1fr)";
          elField.style.gridTemplateAreas = `"play label" "control control"`;

          triggerWrapper.style.gridArea = "play";
          controlsWrapper.style.gridArea = "control";
          if (label != null) label.style.gridArea = "label";

          controls.forEach((el) => controlsWrapper.append(el));

          [...elField.children].forEach((el) => {
            if (el === label || !isHTMLElement(el)) return;
            el.style.gridColumn = "1 / -1";
          });

          elField.append(triggerWrapper, controlsWrapper);
        }
      }

      // Moved this last because it replaces the input/controls conditionally where as the ones above only move
      const inputFields = alpacaForm.domEl[0]?.querySelectorAll<HTMLInputElement>(
        "input[type=date],input[type=text],input[type=radio]",
      );

      for (const inputField of inputFields ?? []) {
        switch (inputField.type) {
          case "text": {
            const textarea = window.value.document.createElement("textarea");

            textarea.id = inputField.id;
            textarea.value = inputField.value;
            textarea.readOnly = textarea.disabled = true;
            textarea.placeholder = inputField.placeholder || " ";
            // Force the element's size to change to contain its text
            textarea.className = "[field-sizing:content] alpaca-control";

            inputField.parentElement?.replaceChild(textarea, inputField);
            break;
          }
          case "date": {
            inputField.type = "text";

            if (/^\d{4,6}-\d{2}-\d{2}$/.test(inputField.value)) {
              const [year = 0, month = 0, day = 0] = inputField.value.split("-");
              inputField.value = `${day} / ${month} / ${year}`;
            }
            break;
          }
          case "radio": {
            // So the selected radio color is not dimmed
            inputField.disabled = false;
            inputField.readOnly = true;

            const wrapper = inputField.closest<HTMLElement>("[data-alpaca-field-path]");

            if (
              wrapper != null &&
              // If this selector matches, we've already added the fieldset and grouped the inputs
              inputField.closest("fieldset > [data-alpaca-field-path]") == null
            ) {
              const fieldset = window.value.document.createElement("fieldset");

              const label = wrapper.querySelector("& > label");
              if (label != null) {
                const legend = window.value.document.createElement("legend");
                legend.textContent = label.textContent;
                legend.className = "tw-sr-only"; // Hide the legend visually, so the label is placed in the grid-area
                label.setAttribute("aria-hidden", "true"); // Hide the label for screen-readers, so the legend is the preferred title
                fieldset.append(legend);
              }

              wrapper.parentElement?.insertBefore(fieldset, wrapper);
              fieldset.append(wrapper); // Move wrapper into fieldset, after the legend
            }

            break;
          }
        }
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateQpackAnswers: function () {},
    setQuestionPackBasePath: function () {
      // Important especially for spellchecker files
      return qPackPublicPath.value;
    },
  };

  return config;
}

function saveEdit() {
  window.value.questionPackAnswers(fileForm.value.form?.answers.bo_interviewTerminatedEarly as boolean | undefined);
}

const edit = () => rerender("edit");
const reloadForm = () => rerender("display");

function rerender(displayMode: DISPLAY_MODE) {
  formMode.value =
    displayMode === "display" || formState.value.status !== "ready"
      ? undefined
      : { mode: displayMode, fileReference: formState.value.fileReference };
  formState.value = { status: "idle" };
  rerenderKey.value++;
}

interface AlpacaForm {
  domEl: HTMLElement[];
  children: AlpacaField[];
  childrenById: Record<string, AlpacaField>;
  getControlByPath(path: string): AlpacaField;
}

type JQueryArray<T> = { toArray(): T[]; [K: number]: T } | undefined;

interface AlpacaField {
  path: string;
  name: string;
  type: string;
  data: unknown;
  domEl: JQueryArray<HTMLElement>;
  field: JQueryArray<HTMLElement>;
  control: JQueryArray<Node>;
  isHidden(): boolean;
}

declare global {
  interface Window {
    loadQuestionPack: () => unknown;
    jQuery: (arg: () => unknown) => void;
    qpackProvider: {
      getQuestionPack: () => string | undefined;
      getQpackAnswers: () => string | undefined;
      getRecordingTimestamp: () => number;
      getRenderMode: () => DISPLAY_MODE;
      rendererHostIdentifier: () => string;
      setQpackAnswers: (newAnswers: string) => any;
      displayPostRender: (alpacaForm: AlpacaForm) => any;
      updateQpackAnswers: () => any;
      setQuestionPackBasePath: () => string;
    };
    questionPackAnswers: (abandon: boolean | undefined) => void;
  }
}
</script>
<template>
  <div v-if="!routeParams.casefile" class="tw-grid tw-size-full tw-place-items-center">
    <div class="tw-max-w-sm tw-text-pretty">No file selected</div>
  </div>
  <div v-else-if="fileForm.status === 'not-exists'" class="tw-grid tw-size-full tw-place-items-center">
    <div class="tw-max-w-sm tw-text-pretty">No form exists for this file</div>
  </div>
  <Loading v-else-if="fileForm.status === 'loading'" id="loading" message="Loading Question Pack" />
  <div
    v-else-if="fileForm.status === 'error' || formState.status === 'error'"
    class="tw-grid tw-size-full tw-place-items-center"
  >
    <div class="tw-flex tw-max-w-sm tw-flex-col tw-items-center tw-gap-3 tw-text-pretty">
      <template v-if="fileForm.status === 'error'">Error loading form</template>
      <template v-else-if="formState.status === 'error'">
        <p>{{ formState.message }}</p>
        <button
          type="button"
          class="tw-bg-gray active tw-b-r tw-rounded-md tw-px-3 tw-py-1 tw-text-xs"
          @click="reloadForm()"
        >
          Retry
        </button>
      </template>
    </div>
  </div>
  <Loading v-else-if="formState.status === 'idle'" id="loading" message="Waiting to start" />
  <Loading v-else-if="formState.status === 'loading-scripts'" id="loading" message="Loading Scripts" />
  <Loading v-else-if="formState.status === 'rendering'" id="loading" message="Rendering Form" />
  <div v-else class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-1" :class="dockview != null && 'tw-size-full'">
    <div id="questionpack-wrapper" class="tw-min-h-0 tw-flex-1 tw-px-2.5 [&_*]:!tw-box-border">
      <div id="questionpack" class="tw-h-full tw-overflow-y-auto"></div>
    </div>
    <div class="tw-flex tw-items-center tw-justify-end tw-space-x-2 tw-px-3 tw-py-2">
      <template v-if="formMode?.mode === 'edit'">
        <div class="tw-flex tw-items-center tw-gap-1">
          <i aria-hidden="true" class="fas fa-sm fa-star" />
          Required
        </div>
        <div class="tw-flex-1" />
        <button
          type="button"
          class="tw-bg-green invert active tw-b-r tw-min-w-20 tw-rounded-md tw-px-2 tw-py-1 tw-text-xs"
          @click="saveEdit()"
        >
          Save
          <i v-if="formState.status === 'saving'" class="fas fa-circle-notch fa-spin fa-sm"></i>
        </button>
        <button
          type="button"
          class="tw-bg-gray active tw-b-r tw-min-w-20 tw-rounded-md tw-px-2 tw-py-1 tw-text-xs"
          @click="reloadForm()"
        >
          Cancel
        </button>
      </template>
      <button
        v-else
        v-can:EditForms
        type="button"
        class="tw-bg-gray tw-b-r tw-rounded-md tw-px-2 tw-py-1 tw-text-xs"
        @click="edit()"
      >
        Edit Form
      </button>
    </div>
  </div>
</template>
<style>
.alpaca-field-hidden {
  display: none !important;
}

#questionpack-wrapper .radio label {
  padding-bottom: 6px !important;
  /* currently this property is 0 in the style, this should match the bottom padding */
  padding-top: 6px !important;
}

#questionpack-wrapper input[type="radio"] {
  /* with the auto appearance, this now replaces the need for the before pseudo element  */
  accent-color: var(--color);
  /* use the default radio button appearance */
  appearance: auto !important;
  /* remove margin used for positioning */
  margin: 0 !important;
  /* remove margin used for positioning */
  top: 0 !important;
}

#questionpack input[type="radio"]:checked::before {
  /* not needed anymore with the auto appearance */
  display: none !important;
}

.alpaca-field-array
  > .alpaca-vertical.alpaca-container-has-items
  > .alpaca-container-item
  label:has(input[type="radio"]):before {
  /* hide the counter numbering */
  display: none !important;
}

@media not print {
  .only-print {
    display: none !important;
  }
}
</style>
